import { z } from 'zod';
import { daysInMonth, isoDate } from './dates.js';
import { MOVEMENT_TYPE_LABELS } from './bank-statements.js';
import { cardMonthLabel } from './credit-cards.js';
import { LOAN_MOVEMENT_KIND_LABELS } from './loans.js';
import { formatDecimal, fromCents, toCents } from './money.js';
import { RECONCILIATION_REASON_LABELS, reconciliationCheck, } from './reconciliation.js';
import { isoDateSchema } from './validation.js';
/*
 * The CSV of the bank movements and of the card spending for the accountant
 * (2.30): «;» between fields, comma decimals without the thousands dot,
 * dd/mm/yyyy dates, UTF-8 with the BOM so Excel reads the accents.
 */
/** The period of an export, both days included. */
export const csvExportQuerySchema = z
    .object({ from: isoDateSchema, to: isoDateSchema })
    .refine((value) => value.from <= value.to, {
    message: 'La data iniziale è dopo quella finale',
    path: ['to'],
});
/** The cards: all of them, or one. */
export const cardCsvExportQuerySchema = z
    .object({ from: isoDateSchema, to: isoDateSchema, cardId: z.uuid().optional() })
    .refine((value) => value.from <= value.to, {
    message: 'La data iniziale è dopo quella finale',
    path: ['to'],
});
export function csvExportPeriods(today) {
    const year = Number(today.slice(0, 4));
    const month = Number(today.slice(5, 7));
    const monthPeriod = (y, m) => ({
        from: isoDate(y, m, 1),
        to: isoDate(y, m, daysInMonth(y, m)),
    });
    const quarter = Math.floor((month - 1) / 3);
    const lastQuarterYear = quarter === 0 ? year - 1 : year;
    const lastQuarterStart = quarter === 0 ? 10 : (quarter - 1) * 3 + 1;
    return [
        {
            label: 'Mese scorso',
            ...monthPeriod(month === 1 ? year - 1 : year, month === 1 ? 12 : month - 1),
        },
        { label: 'Mese in corso', ...monthPeriod(year, month) },
        {
            label: 'Trimestre scorso',
            from: isoDate(lastQuarterYear, lastQuarterStart, 1),
            to: monthPeriod(lastQuarterYear, lastQuarterStart + 2).to,
        },
        { label: 'Anno scorso', from: isoDate(year - 1, 1, 1), to: isoDate(year - 1, 12, 31) },
    ];
}
/** «movimenti-intesa-2026-09-01-2026-09-30.csv». */
export function csvExportFileName(prefix, query) {
    const name = prefix
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    return `${name}-${query.from}-${query.to}.csv`;
}
/** 2026-09-01 → 01/09/2026. */
export function csvDate(value) {
    if (!value)
        return '';
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
}
/** «-4118,98»; with `signed` also «+179,20», for who reads every number without a sign as money in. */
export function csvAmount(value, signed = false) {
    const cents = toCents(value);
    const text = formatDecimal(cents / 100, 2, 2, false);
    return signed && cents > 0 ? `+${text}` : text;
}
/** First in the file: Excel then reads it as UTF-8, with the accents. */
export const CSV_BOM = String.fromCharCode(0xfeff);
/** A field on one line, in quotes when it holds the separator or a quote. */
function csvField(value) {
    const text = value.replace(/\s*[\r\n]+\s*/g, ' ');
    return /[;"]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}
function csvDocument(header, rows) {
    const lines = [header, ...rows].map((row) => row.map(csvField).join(';'));
    return `${CSV_BOM}${lines.join('\r\n')}\r\n`;
}
function summaryOf(amounts) {
    let incoming = 0;
    let outgoing = 0;
    for (const amount of amounts) {
        const cents = toCents(amount);
        if (cents > 0)
            incoming += cents;
        else
            outgoing += cents;
    }
    return { rows: amounts.length, incoming: fromCents(incoming), outgoing: fromCents(outgoing) };
}
/* --- Bank movements ---------------------------------------------------------------- */
/** An advance on invoices, its repayment or an unpaid (1.62), in a few words. */
export function advanceLabel(advance) {
    const invoices = advance.invoices.map((item) => item.number).join(', ');
    if (advance.kind === 'estinzione')
        return 'anticipo estinto da noi';
    if (advance.kind === 'interessi')
        return 'interessi sugli anticipi';
    if (advance.kind === 'erogazione') {
        return `anticipo fatture${invoices ? ` · ${invoices}` : ' · fatture da indicare'}`;
    }
    const what = advance.kind === 'rimborso' ? 'rimborso anticipo' : 'insoluto su anticipo';
    return `${what} · fattura ${invoices || advance.invoiceNumber}`;
}
/** With what a movement is reconciled, in a few words: «2 fatture», «stipendio Rossi». */
export function reconciliationWhat(done) {
    // The parts on subjects without a document (2.22): «senza documento · Rossi».
    const waiting = (done.subjectPayments ?? []).filter((item) => toCents(item.available) > 0);
    const onSubjects = waiting.length
        ? `senza documento · ${[...new Set(waiting.map((item) => item.subjectName))].join(', ')}`
        : '';
    // To subjects that do not invoice (3.14): the cost itself.
    const costs = (done.subjectPayments ?? []).filter((item) => item.withoutInvoice);
    const withoutInvoice = costs.length
        ? `senza fattura · ${[...new Set(costs.map((item) => item.subjectName))].join(', ')}`
        : '';
    if (done.kind === 'fatture') {
        return [
            done.invoices.length === 0
                ? ''
                : done.invoices.length === 1
                    ? done.invoices[0].number
                    : `${done.invoices.length} fatture`,
            done.rest?.note ?? '',
            onSubjects,
            withoutInvoice,
        ]
            .filter(Boolean)
            .join(' + ');
    }
    if (done.kind === 'stipendi') {
        return done.salaries && done.salaries.length === 1
            ? `stipendio ${done.salaries[0].name}`
            : done.salaries
                ? `${done.salaries.length} stipendi`
                : 'stipendio';
    }
    if (done.kind === 'tfr') {
        return done.tfr && done.tfr.length === 1
            ? `TFR ${done.tfr[0].name}`
            : done.tfr
                ? `TFR di ${done.tfr.length} dipendenti`
                : 'TFR';
    }
    if (done.kind === 'mutuo' && done.loan) {
        const label = LOAN_MOVEMENT_KIND_LABELS[done.loan.kind].toLowerCase();
        return `${label}${done.loan.reversedOn ? ' stornata' : ''} · ${done.loan.name}`;
    }
    if (done.reversal) {
        return `storno della rata${done.reversal.loanName ? ` · ${done.reversal.loanName}` : ''}`;
    }
    if (done.kind === 'anticipo' && done.advance)
        return advanceLabel(done.advance);
    if (done.kind === 'carte' && done.cards) {
        return `carte di credito · spese di ${cardMonthLabel(done.cards.month)}`;
    }
    if (done.kind === 'spese')
        return 'spese bancarie';
    if (done.kind === 'rateazione') {
        const items = done.instalments ?? [];
        return items.length === 1
            ? `rata ${items[0].number} · ${items[0].planName}`
            : `${items.length} rate di rateazioni`;
    }
    return done.reason ? RECONCILIATION_REASON_LABELS[done.reason].toLowerCase() : 'altro';
}
/** The column «Riconciliazione»: «da riconciliare», «✓ 2 fatture», «◐ …» when it does not add up. */
export function reconciliationText(movement) {
    const done = movement.reconciliation;
    if (!done)
        return 'da riconciliare';
    const check = reconciliationCheck(movement.amount, done);
    const short = check !== null && toCents(check.unassigned) !== 0;
    return `${short ? '◐' : '✓'} ${reconciliationWhat(done)}`;
}
/** The movements of the period, both days included. */
export function movementsBetween(movements, query) {
    return movements.filter((item) => item.bookingDate >= query.from && item.bookingDate <= query.to);
}
export function bankMovementsSummary(movements) {
    return summaryOf(movements.map((item) => item.amount));
}
/** The movements as the bank writes them: money out with «-», money in without a sign. */
export function bankMovementsCsv(movements) {
    return csvDocument([
        'Data contabile',
        'Data valuta',
        'Tipo',
        'Descrizione della banca',
        'Importo',
        'Saldo',
        'Riconciliazione',
    ], movements.map((item) => [
        csvDate(item.bookingDate),
        csvDate(item.valueDate),
        `${MOVEMENT_TYPE_LABELS[item.type]}${item.abiCode ? ` causale ${item.abiCode}` : ''}`,
        item.counterparty && !item.description.includes(item.counterparty)
            ? `${item.counterparty} · ${item.description}`
            : item.description,
        csvAmount(item.amount),
        csvAmount(item.balance),
        reconciliationText(item),
    ]));
}
/* --- Card spending ------------------------------------------------------------------- */
/**
 * The amount of a spending as money of the current account: the bank of the
 * card writes a spending positive and a refund negative, the accountant's
 * program wants the spending negative and the refund positive (22/10/2026).
 */
export function cardAccountAmount(amount) {
    return fromCents(-toCents(amount));
}
export function cardMovementsSummary(items) {
    return summaryOf(items.map((item) => cardAccountAmount(item.amount)));
}
function reclassLabel(items, code) {
    if (!code)
        return '—';
    const item = items.find((choice) => choice.code === code);
    return item ? `${item.code} ${item.description}` : code;
}
/** The column «Rendicontazione»: «fattura 12 · conto 421 Software · Fornitore», «da rendicontare». */
export function cardAccountingText(item, items) {
    const done = item.reconciliation;
    if (!done)
        return 'da rendicontare';
    const conto = done.reclassCode ? `conto ${reclassLabel(items, done.reclassCode)}` : '';
    const invoices = done.invoices.map((invoice) => invoice.number).join(', ');
    if (done.kind === 'soggetto') {
        return ['al soggetto', done.subject?.name ?? '', conto].filter(Boolean).join(' · ');
    }
    if (done.kind === 'rata') {
        const dues = (done.instalments ?? []).map((due) => `rata ${due.number} ${due.planName}`);
        return [...dues, conto].filter(Boolean).join(' · ');
    }
    if (done.kind === 'fatture') {
        return [`fattura ${invoices}`, conto, done.invoices[0]?.counterpartyName ?? '']
            .filter(Boolean)
            .join(' · ');
    }
    return [
        `conto ${reclassLabel(items, done.reclassCode)}`,
        item.expectsInvoice || item.proposal ? 'fattura da collegare' : '',
        done.note ?? '',
    ]
        .filter(Boolean)
        .join(' · ');
}
/** The spending with the sign of the current account, always written: «-780,19», «+179,20». */
export function cardMovementsCsv(movements, items) {
    return csvDocument([
        'Data',
        'Carta',
        'Titolare',
        'Descrizione della banca',
        'Categoria',
        'Importo originale',
        'Euro',
        'Rendicontazione',
    ], movements.map((item) => [
        csvDate(item.date),
        item.last4,
        item.holder,
        item.description,
        item.category ?? (item.mcc ? `Codice ${item.mcc}` : ''),
        item.currency === 'EUR'
            ? ''
            : [
                csvAmount(cardAccountAmount(item.originalAmount), true),
                item.currency,
                item.exchangeRate
                    ? `cambio ${item.exchangeRate.replace('.', ',').replace(/0+$/, '')}`
                    : '',
            ]
                .filter(Boolean)
                .join(' '),
        csvAmount(cardAccountAmount(item.amount), true),
        cardAccountingText(item, items),
    ]));
}
//# sourceMappingURL=treasury-csv.js.map